import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiService } from '../api/api.service';
import { OfferBrandingSettings, Phase, Project } from '../models/project.model';
import { API_CONFIG } from '../config/api.config';
import { map, switchMap } from 'rxjs/operators';
import { stratusFileProxyUrl } from '../../shared/utils/stratus-url.util';

export interface ProjectListResult {
    items: Project[];
    total: number;
    page: number;
    perPage: number;
}

export interface ProjectHoldSettingsMember {
    id: string;
    name: string | null;
    email: string | null;
}

export interface ProjectHoldSettings {
    projectId: string;
    orgId: string | null;
    enabled: boolean;
    holdDays: number;
    extensionHours: number;
    approverUserIds: string[];
    updatedAt: string | null;
    members: ProjectHoldSettingsMember[];
}

export interface ProjectHoldSettingsPayload {
    enabled: boolean;
    hold_days: number;
    extension_hours: number;
    approver_user_ids: string[];
}

export interface PhaseListResult {
    items: Phase[];
    total: number;
    page: number;
    perPage: number;
}

export type OfferSlideKind =
    | 'about'
    | 'tagline'
    | 'phases'
    | 'contact'
    | 'closing'
    | 'appendix';

export type ProjectOfferSlide = {
    id: string;
    project_id: string;
    property_id?: string | null;
    kind: OfferSlideKind | string;
    file_id: string;
    sort_order: number;
    meta_json: any | null;
    width_px?: number | null;
    height_px?: number | null;
    aspect_label?: string | null;
    url: string | null;
    created_at?: string | null;
};

@Injectable({
    providedIn: 'root'
})
export class ProjectsService {
    private http = inject(HttpClient);
    private api = inject(ApiService);
    private readonly PUBLIC_PATH = `${API_CONFIG.PUBLIC_URL}/projects`;
    private readonly AUTH_PATH = '/API2/projects';
    private readonly AUTH_PHASES_PATH = '/API2/phases';

    // Public listing for marketing pages
    getProjects(options: { page?: number; perPage?: number } = {}): Observable<ProjectListResult> {
        const perPage = options.perPage ?? 10;
        const page = options.page ?? 1;
        const params = new HttpParams()
            .set('per_page', String(perPage))
            .set('page', String(page))
            .set('sort_by', 'MODIFIEDTIME')
            .set('sort_order', 'desc');

        return this.fetchPublic<any>(this.PUBLIC_PATH, params).pipe(
            map(response => this.normalizeListResponse(response, page, perPage)),
            map(({ items, meta }) => ({
                items: items.map((item: any) => this.mapProject(item)),
                total: meta.total,
                page: meta.page,
                perPage: meta.perPage
            }))
        );
    }

    // Authenticated listing for dashboard flows
    getProjectsAuth(options: { page?: number; perPage?: number } = {}): Observable<ProjectListResult> {
        return this.getProjectsAuthFiltered({}, { ...options });
    }

    getProjectsAuthFiltered(
        filter: { status?: string; isApproved?: boolean; isDeleted?: boolean; search?: string } = {},
        options: { pendingApproval?: boolean; includeDeleted?: boolean; page?: number; perPage?: number } = {}
    ): Observable<ProjectListResult> {
        const perPage = options.perPage ?? 50;
        const page = options.page ?? 1;
        const baseParams: Record<string, string> = {
            per_page: String(perPage),
            page: String(page),
            sort_by: 'MODIFIEDTIME',
            sort_order: 'desc'
        };

        if (filter.status !== undefined && filter.status !== null && filter.status !== '') {
            baseParams['status'] = filter.status.toString();
        }
        if (filter.isApproved !== undefined && filter.isApproved !== null) {
            baseParams['is_approved'] = filter.isApproved.toString();
        }
        if (filter.isDeleted !== undefined && filter.isDeleted !== null) {
            baseParams['is_deleted'] = filter.isDeleted.toString();
        }
        if (filter.search !== undefined && filter.search !== null && filter.search !== '') {
            baseParams['search'] = filter.search.toString();
        }

        if (options.pendingApproval) {
            baseParams['pending_approval'] = 'true';
        }
        if (options.includeDeleted) {
            baseParams['include_deleted'] = 'true';
        }

        const params = new HttpParams({ fromObject: baseParams });

        return this.api.get<any>(this.AUTH_PATH, params).pipe(
            map(response => this.normalizeListResponse(response, page, perPage)),
            map(({ items, meta }) => ({
                items: items.map((item: any) => this.mapProject(item)),
                total: meta.total,
                page: meta.page,
                perPage: meta.perPage
            }))
        );
    }

    getProject(id: string): Observable<Project> {
        const params = new HttpParams();

        return this.fetchPublic<any>(`${this.PUBLIC_PATH}/${id}`, params).pipe(
            map(response => this.mapProject(response?.data || response))
        );
    }

    getProjectAuth(id: string): Observable<Project> {
        return this.api.get<any>(`${this.AUTH_PATH}/${id}`).pipe(
            map(response => this.mapProject(response?.data || response))
        );
    }

    getPhasesByProject(projectId: string, options: { page?: number; perPage?: number } = {}): Observable<Phase[]> {
        const perPage = options.perPage ?? 10;
        const page = options.page ?? 1;
        const params = new HttpParams()
            .set('project_id', projectId)
            .set('per_page', String(perPage))
            .set('page', String(page))
            .set('sort_by', 'delivery_date')
            .set('sort_order', 'asc');
        return this.fetchPublic<any>(`${API_CONFIG.PUBLIC_URL}/phases`, params).pipe(
            map(response => this.normalizeListResponse(response, page, perPage).items.map((row: any) => this.mapPhase(row)))
        );
    }

    getPhasesByProjectAuth(projectId: string, options: { page?: number; perPage?: number } = {}): Observable<Phase[]> {
        const perPage = options.perPage ?? 50;
        const page = options.page ?? 1;
        const params = new HttpParams()
            .set('project_id', projectId)
            .set('per_page', String(perPage))
            .set('page', String(page))
            .set('sort_by', 'delivery_date')
            .set('sort_order', 'asc');
        return this.api.get<any>(this.AUTH_PHASES_PATH, params).pipe(
            map(response => this.normalizeListResponse(response, page, perPage).items.map((row: any) => this.mapPhase(row)))
        );
    }

    getPhasesAuth(options: { page?: number; perPage?: number; projectId?: string } = {}): Observable<PhaseListResult> {
        const perPage = options.perPage ?? 50;
        const page = options.page ?? 1;
        const paramsBase: Record<string, string> = {
            per_page: String(perPage),
            page: String(page),
            sort_by: 'delivery_date',
            sort_order: 'asc'
        };
        const projectId = options.projectId ? String(options.projectId).trim() : '';
        if (projectId) paramsBase['project_id'] = projectId;
        const params = new HttpParams({ fromObject: paramsBase });

        return this.api.get<any>(this.AUTH_PHASES_PATH, params).pipe(
            map((response) => this.normalizeListResponse(response, page, perPage)),
            map(({ items, meta }) => ({
                items: items.map((row: any) => this.mapPhase(row)),
                total: meta.total,
                page: meta.page,
                perPage: meta.perPage
            }))
        );
    }

    createPhase(projectId: string, phase: { name: string; deliveryDate: string; status?: string | null }): Observable<Phase> {
        return this.api.post<any>(`${this.AUTH_PHASES_PATH}`, {
            name: phase.name,
            project_id: projectId,
            status: phase.status ?? 'Active',
            delivery_date: phase.deliveryDate
        }).pipe(
            map((response) => this.mapPhase(response?.data || response))
        );
    }

    updatePhase(id: string, phase: { name: string; deliveryDate: string; status?: string | null }): Observable<Phase> {
        const payload: Record<string, unknown> = {
            name: phase.name,
            delivery_date: phase.deliveryDate
        };
        if (phase.status !== undefined) payload['status'] = phase.status;

        return this.api.put<any>(`${this.AUTH_PHASES_PATH}/${id}`, payload).pipe(
            map((response) => this.mapPhase(response?.data || response))
        );
    }

    deletePhase(id: string): Observable<void> {
        return this.api.delete<void>(`${this.AUTH_PHASES_PATH}/${id}`);
    }

    private fetchPublic<T>(url: string, params: HttpParams): Observable<T> {
        const query = params.toString();
        const fullUrl = query ? `${url}?${query}` : url;

        return from(fetch(fullUrl, {
            credentials: 'omit',
            headers: {
                'Accept': 'application/json'
            }
        })).pipe(
            switchMap(async (res) => {
                if (!res.ok) {
                    const message = await res.text().catch(() => res.statusText);
                    throw new Error(message || `Request failed with status ${res.status}`);
                }
                return res.json() as Promise<T>;
            })
        );
    }

    private mapProject(data: any): Project {
        const normalizeAssetUrl = (value: any): string | null => {
            const raw = value === null || value === undefined ? '' : String(value).trim();
            if (!raw) return null;
            return stratusFileProxyUrl(raw) || raw;
        };

        const appendAssetVersion = (value: string | null, versionRaw: unknown): string | null => {
            const url = String(value || '').trim();
            const version = String(versionRaw || '').trim();
            if (!url || !version) return value;
            try {
                const isAbsolute = /^[a-z]+:\/\//i.test(url);
                const base = typeof window !== 'undefined' ? window.location.origin : 'https://app.nibni.com';
                const parsed = new URL(url, base);
                parsed.searchParams.set('v', version);
                if (isAbsolute) return parsed.toString();
                return `${parsed.pathname}${parsed.search}${parsed.hash}`;
            } catch {
                return value;
            }
        };

        const normalizeDateOnly = (value: any): string | null => {
            const raw = value === null || value === undefined ? '' : String(value).trim();
            if (!raw) return null;
            const dateOnly = raw.slice(0, 10);
            return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : raw;
        };

        const formatMonthYear = (value: string): string => {
            const dateOnly = value.slice(0, 10);
            const d = new Date(`${dateOnly}T00:00:00Z`);
            if (Number.isNaN(d.getTime())) return value;
            try {
                return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(d);
            } catch {
                return value;
            }
        };

        const lookups = data?.__lookups ?? {};
        const addressRaw = data?.address ?? data?.ADDRESS_ID ?? lookups.ADDRESS_ID ?? null;
        const addressObject =
            addressRaw && typeof addressRaw === 'object' && Object.keys(addressRaw).length
                ? addressRaw
                : null;
        const vendor = lookups.PROFILE_ID || data?.PROFILE_ID || {};
        const organization = lookups.ORGANIZATION_ID || data?.ORGANIZATION_ID || {};
        const creatorLookup = lookups.CREATORID || lookups.CREATOR || lookups.CREATED_BY || data?.CREATOR || {};
        const propertiesChild = Array.isArray(data?.PROPERTIES)
            ? data.PROPERTIES.filter((prop: any) => {
                const flag = prop?.IS_DELETED ?? prop?.is_deleted;
                if (flag === null || flag === undefined) return true;
                const normalized = String(flag).toLowerCase();
                return !(normalized === 'true' || normalized === '1' || normalized === 'yes');
            })
            : [];

        const parseNumber = (value: unknown): number | null => {
            if (typeof value === 'number' && Number.isFinite(value)) return value;
            if (typeof value === 'string') {
                const parsed = parseFloat(value);
                return Number.isFinite(parsed) ? parsed : null;
            }
            return null;
        };
        const parseBoolean = (value: unknown): boolean | null => {
            if (typeof value === 'boolean') return value;
            if (typeof value === 'string') {
                const normalized = value.trim().toLowerCase();
                if (['true', '1', 'yes'].includes(normalized)) return true;
                if (['false', '0', 'no'].includes(normalized)) return false;
            }
            if (typeof value === 'number') return value === 1;
            return null;
        };
        const formatPriceLabel = (value: number, currency?: string | null): string => {
            const formatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            const amount = formatter.format(value);
            return currency ? `${currency} ${amount}` : amount;
        };

        const id = String(data?.ROWID || data?.id || '');
        const isApproved = parseBoolean(data?.IS_APPROVED ?? data?.is_approved);
        const isVerified = parseBoolean(data?.IS_VERIFIED ?? data?.is_verified ?? data?.isVerified);
        const isPublic = parseBoolean(data?.IS_PUBLIC ?? data?.is_public ?? data?.isPublic);
        const isDeleted = parseBoolean(data?.IS_DELETED ?? data?.is_deleted ?? data?.isDeleted);
        const reservationsEnabled = parseBoolean(
            data?.RESERVATIONS_ENABLED ?? data?.reservations_enabled ?? data?.reservationsEnabled
        );
        const investorRequestOffersEnabled = parseBoolean(
            data?.INVESTOR_REQUEST_OFFERS_ENABLED ?? data?.investor_request_offers_enabled ?? data?.investorRequestOffersEnabled
        );
        const partnerRequestOffersEnabled = parseBoolean(
            data?.PARTNER_REQUEST_OFFERS_ENABLED ?? data?.partner_request_offers_enabled ?? data?.partnerRequestOffersEnabled
        );
        const rawImages = (data?.IMAGES || data?.images || []) as Array<any>;
        const coverFileId = data?.COVER_FILE_ID || data?.cover_file_id || data?.coverFileId || null;
        const offerCoverFileId = data?.OFFER_COVER_FILE_ID || data?.offer_cover_file_id || data?.offerCoverFileId || coverFileId || null;
        const mediaVersion = data?.MODIFIEDTIME || data?.modified_time || data?.updated_at || coverFileId || offerCoverFileId || null;
        const storedImage = appendAssetVersion(
            normalizeAssetUrl(data?.IMAGE_URL || data?.image_url || data?.IMAGE || data?.image || null),
            coverFileId || mediaVersion
        );
        const offerCoverUrlRaw = appendAssetVersion(
            normalizeAssetUrl(data?.OFFER_COVER_URL || data?.offerCoverUrl || data?.offer_cover_url || null),
            offerCoverFileId || mediaVersion
        );
        const images = rawImages
            .map((img: any) =>
                appendAssetVersion(
                    normalizeAssetUrl(img?.URL || img?.url || img?.IMAGE || img?.image),
                    img?.FILE_ID || img?.file_id || img?.ROWID || img?.row_id || img?.id || mediaVersion
                )
            )
            .filter((image: string | null): image is string => Boolean(image));
        const primaryImage = storedImage || images[0] || offerCoverUrlRaw || null;
        const offerCoverUrl = offerCoverUrlRaw || primaryImage || null;
        const offerBranding = this.mapOfferBranding(data?.OFFER_BRANDING_JSON ?? data?.offer_branding_json ?? data?.offerBranding ?? null);

        const countryRaw =
            data?.COUNTRY ??
            data?.country ??
            data?.COUNTRY_CODE ??
            data?.country_code ??
            addressObject?.COUNTRY ??
            addressObject?.country ??
            null;
        const country = countryRaw === null || countryRaw === undefined ? null : String(countryRaw).trim() || null;
        const normalizedCountry = country ? country.trim().toUpperCase() : null;
        const countryCode = normalizedCountry && /^[A-Z]{2}$/.test(normalizedCountry) ? normalizedCountry : null;
        const countryLabel = this.countryNameFromCode(countryCode || country);
        const countryName = countryLabel || country || null;
        const locationLegacyRaw = data?.LOCATION ?? data?.location ?? null;
        const locationLegacy = locationLegacyRaw === null || locationLegacyRaw === undefined ? null : String(locationLegacyRaw).trim() || null;
        const locationLabelSeed = locationLegacy || countryLabel || country || null;

        type PropertyPrice = {
            price: number | null;
            currency: string | null;
            ready: boolean | null;
            delivery: string | null;
            paymentPlans: any[];
        };

        const projectDeliveryDate = normalizeDateOnly(
            data?.DELIVERY_DATE ?? data?.delivery_date ?? data?.deliveryDate ?? data?.DELIVERYDATE ?? null
        );

        const propertyPrices: PropertyPrice[] = propertiesChild
            .map((prop: any): PropertyPrice => ({
                price: parseNumber(prop?.PRICE || prop?.price),
                currency: prop?.CURRENCY_ID?.CODE || prop?.CURRENCY?.CODE || prop?.CURRENCY || null,
                ready: parseBoolean(prop?.READY_TO_MOVE),
                delivery: normalizeDateOnly(prop?.DELIVERY_DATE || prop?.delivery_date || prop?.deliveryDate || null),
                paymentPlans: Array.isArray(prop?.PAYMENT_PLANS) ? prop.PAYMENT_PLANS : []
            }))
            .filter((entry: PropertyPrice) => entry.price !== null);

        let startingPrice: number | null = null;
        let startingCurrency: string | null = null;
        if (propertyPrices.length) {
            const cheapest = propertyPrices.reduce<PropertyPrice>((lowest, current) => {
                if (lowest.price === null) return current;
                if (current.price !== null && current.price < (lowest.price || Infinity)) return current;
                return lowest;
            }, { price: null, currency: null, ready: null, delivery: null, paymentPlans: [] });
            startingPrice = cheapest.price;
            startingCurrency = cheapest.currency || null;
        }
        if (startingPrice === null) {
            const fallbackPrice = parseNumber(data?.STARTING_PRICE ?? data?.starting_price ?? data?.startingPrice);
            const fallbackCurrency = data?.STARTING_CURRENCY_CODE ?? data?.starting_currency_code ?? data?.startingCurrencyCode;
            if (fallbackPrice !== null) {
                startingPrice = fallbackPrice;
                startingCurrency = fallbackCurrency ? String(fallbackCurrency).toUpperCase() : null;
            }
        }

        const readyProps = propertyPrices.filter((p: PropertyPrice) => p.ready === true);
        const deliveryYears = propertyPrices
            .map((p: PropertyPrice) => (p.delivery ? new Date(p.delivery) : null))
            .filter((d: Date | null): d is Date => !!d && !isNaN(d.getTime()))
            .map((d: Date) => d.getFullYear());
        const soonestYear = deliveryYears.length ? Math.min(...deliveryYears) : null;
        let deliveryStatus: string | null = null;
        if (projectDeliveryDate) {
            deliveryStatus = formatMonthYear(projectDeliveryDate);
        } else if (readyProps.length) {
            deliveryStatus = 'Ready to Move';
        } else if (soonestYear) {
            deliveryStatus = `Under Construction – Delivery ${soonestYear}`;
        } else if (typeof data?.STATUS === 'string') {
            deliveryStatus = data.STATUS.replace(/_/g, ' ');
        }

        const highlightsSet = new Set<string>();
        const rawFeatures = Array.isArray(data?.FEATURES) ? data.FEATURES : Array.isArray(data?.features) ? data.features : [];
        rawFeatures.forEach((feat: any) => {
            const label = feat?.NAME || feat?.name || (typeof feat === 'string' ? feat : null);
            if (label) highlightsSet.add(label);
        });
        if (propertiesChild.some((prop: any) => Array.isArray(prop?.PAYMENT_PLANS) && prop.PAYMENT_PLANS.length)) {
            highlightsSet.add('Payment Plans');
        }
        if (readyProps.length) {
            highlightsSet.add('Ready to Move');
        }
        if (!readyProps.length && (projectDeliveryDate || deliveryStatus?.toLowerCase().includes('under construction'))) {
            highlightsSet.add('Under Construction');
        }
        if (locationLabelSeed) {
            highlightsSet.add('Prime Location');
        }
        const highlights = Array.from(highlightsSet).slice(0, 6);

        const locationAddressRaw = data?.LOCATION_ADDRESS ?? data?.location_address ?? data?.locationAddress ?? null;
        const locationAddress = locationAddressRaw === null || locationAddressRaw === undefined
            ? null
            : String(locationAddressRaw).trim() || null;
        const locationLinkRaw = data?.LOCATION_LINK ?? data?.location_link ?? data?.locationLink ?? null;
        const locationLink = locationLinkRaw === null || locationLinkRaw === undefined
            ? null
            : String(locationLinkRaw).trim() || null;
        const locationLabel = locationAddress || locationLegacy || countryLabel || country || null;

        return {
            id,
            name: data?.NAME || data?.name || 'Untitled Project',
            description: data?.DESCRIPTION || data?.description || null,
            locationAddress,
            locationLink,
            country: countryName,
            countryCode,
            countryName,
            status: data?.STATUS || data?.status || 'Active',
            isApproved,
            isVerified,
            isPublic,
            isDeleted,
            reservationsEnabled,
            investorRequestOffersEnabled,
            partnerRequestOffersEnabled,
            ownerUserId: data?.OWNER_USER_ID ?? data?.owner_user_id ?? data?.ownerUserId ?? null,
            createdByUserId: data?.CREATED_BY_USER_ID ?? data?.created_by_user_id ?? data?.createdByUserId ?? null,
            sharedWithOrg: parseBoolean(data?.SHARED_WITH_ORG ?? data?.shared_with_org ?? data?.sharedWithOrg),
            organizationId: organization?.ROWID || organization?.row_id || data?.ORGANIZATION_ID || null,
            organizationName: data?.ORGANIZATION_NAME || organization?.NAME || organization?.name || null,
            location: locationLabel,
            locationLabel,
            image: primaryImage,
            images: primaryImage ? [primaryImage, ...images.filter((image) => image !== primaryImage)] : images,
            features: data?.features || [],
            vendorName: data?.VENDOR_NAME || vendor?.FULL_NAME || vendor?.full_name || vendor?.NAME || vendor?.name || data?.ORGANIZATION_NAME || organization?.NAME || organization?.name || null,
            vendorLogo: data?.VENDOR_LOGO || vendor?.LOGO_URL || vendor?.logo_url || vendor?.logo || vendor?.IMAGE_URL || vendor?.image_url || vendor?.LOGO || null,
            vendorId: vendor?.ROWID || vendor?.row_id || vendor?.id || data?.PROFILE_ID || null,
            vendorEmail: vendor?.EMAIL || vendor?.email || vendor?.contact_email || null,
            vendorPhone: vendor?.MOBILE || vendor?.mobile || null,
            creatorId: data?.CREATORID || creatorLookup?.ROWID || creatorLookup?.row_id || null,
            creatorName: creatorLookup?.NAME || creatorLookup?.name || creatorLookup?.DISPLAY_VALUE || creatorLookup?.display_value || null,
            propertyCount: propertiesChild.length,
            highlights,
            deliveryStatus,
            deliveryDate: projectDeliveryDate,
            startingPrice,
            startingCurrency,
            startingPriceLabel: startingPrice !== null ? formatPriceLabel(startingPrice, startingCurrency) : null,
            developmentBadge: data?.DEVELOPMENT_BADGE ?? data?.development_badge ?? data?.developmentBadge ?? null,
            brochureFileId: data?.BROCHURE_FILE_ID || data?.brochure_file_id || data?.brochureFileId || null,
            coverFileId,
            masterPlanFileId: data?.MASTER_PLAN_FILE_ID || data?.master_plan_file_id || data?.masterPlanFileId || null,
            offerCoverFileId,
            locationMapFileId: data?.LOCATION_MAP_FILE_ID || data?.location_map_file_id || data?.locationMapFileId || null,
            secondPhasePlanFileId: data?.SECOND_PHASE_PLAN_FILE_ID || data?.second_phase_plan_file_id || data?.secondPhasePlanFileId || null,
            detailedPhaseFileId: data?.DETAILED_PHASE_FILE_ID || data?.detailed_phase_file_id || data?.detailedPhaseFileId || null,
            unitPlanFileId: data?.UNIT_PLAN_FILE_ID || data?.unit_plan_file_id || data?.unitPlanFileId || null,
            brochureUrl: normalizeAssetUrl(data?.BROCHURE_URL || data?.brochureUrl || null),
            coverUrl: storedImage,
            masterPlanUrl: normalizeAssetUrl(data?.MASTER_PLAN_URL || data?.masterPlanUrl || null),
            offerCoverUrl,
            locationMapUrl: normalizeAssetUrl(data?.LOCATION_MAP_URL || data?.locationMapUrl || null),
            secondPhasePlanUrl: normalizeAssetUrl(data?.SECOND_PHASE_PLAN_URL || data?.secondPhasePlanUrl || null),
            detailedPhaseUrl: normalizeAssetUrl(data?.DETAILED_PHASE_URL || data?.detailedPhaseUrl || null),
            unitPlanUrl: normalizeAssetUrl(data?.UNIT_PLAN_URL || data?.unitPlanUrl || null),
            offerTemplateCode: data?.OFFER_TEMPLATE_CODE || data?.offer_template_code || data?.offerTemplateCode || null,
            offerLogoFileId: data?.OFFER_LOGO_FILE_ID || data?.offer_logo_file_id || data?.offerLogoFileId || null,
            offerLogoUrl: normalizeAssetUrl(data?.OFFER_LOGO_URL || data?.offerLogoUrl || null),
            offerBranding,
            maintenanceDueDate: normalizeDateOnly(
                data?.MAINTENANCE_DUE_DATE ?? data?.maintenance_due_date ?? data?.maintenanceDueDate ?? null
            ),
            created_at: data?.CREATEDTIME || null,
            updated_at: data?.MODIFIEDTIME || null
        };
    }

    private mapOfferBranding(value: any): OfferBrandingSettings | null {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
        const modeRaw = String(value?.mode || '').trim().toLowerCase();
        const scopeRaw = String(value?.scope || '').trim().toLowerCase();
        const mode = (['none', 'logo', 'company_name'].includes(modeRaw) ? modeRaw : 'logo') as OfferBrandingSettings['mode'];
        const scope = (scopeRaw === 'all' ? 'all' : 'selected') as OfferBrandingSettings['scope'];
        const slideInput = Array.isArray(value?.slide_keys) ? value.slide_keys : Array.isArray(value?.slideKeys) ? value.slideKeys : [];
        const slideKeys: string[] = Array.from(
            new Set(
                slideInput
                    .map((entry: any) => String(entry || '').trim().toLowerCase())
                    .filter(Boolean)
            )
        );
        const companyNameTextRaw = value?.company_name_text ?? value?.companyNameText ?? null;
        const companyNameText = companyNameTextRaw === null || companyNameTextRaw === undefined
            ? null
            : String(companyNameTextRaw).trim() || null;
        const showCoverProjectNameRaw = value?.show_cover_project_name ?? value?.showCoverProjectName;
        const showCoverProjectName = showCoverProjectNameRaw === undefined || showCoverProjectNameRaw === null
            ? true
            : ['false', '0', 'no', 'off'].includes(String(showCoverProjectNameRaw).trim().toLowerCase())
                ? false
                : true;
        const showInstallmentPercentRaw = value?.show_installment_percent ?? value?.showInstallmentPercent;
        const showInstallmentPercent = showInstallmentPercentRaw === undefined || showInstallmentPercentRaw === null
            ? true
            : ['false', '0', 'no', 'off'].includes(String(showInstallmentPercentRaw).trim().toLowerCase())
                ? false
                : true;
        const contactEmail = String(value?.contact_email ?? value?.contactEmail ?? '').trim() || null;
        const contactPhone = String(value?.contact_phone ?? value?.contactPhone ?? '').trim() || null;
        const contactWebsite = String(value?.contact_website ?? value?.contactWebsite ?? '').trim() || null;
        return {
            mode,
            scope,
            slideKeys,
            companyNameText,
            showCoverProjectName,
            showInstallmentPercent,
            contactEmail,
            contactPhone,
            contactWebsite
        };
    }

    private countryNameFromCode(value: string | null | undefined): string | null {
        if (!value) return null;
        const normalized = String(value).trim();
        if (!normalized) return null;
        const upper = normalized.toUpperCase();
        if (!/^[A-Z]{2}$/.test(upper)) return normalized;
        if (upper === 'EG') return 'Egypt';
        try {
            const display = new (Intl as any).DisplayNames(['en'], { type: 'region' });
            return display.of(upper) || upper;
        } catch {
            return upper;
        }
    }

    private mapPhase(row: any): Phase {
        return {
            id: String(row?.ROWID || row?.id || ''),
            name: row?.Name || row?.NAME || row?.name || 'Phase',
            status: row?.STATUS || row?.status || null,
            project_id: row?.PROJECT_ID?.ROWID || row?.PROJECT_ID || row?.project_id || null,
            deliveryDate: (() => {
                const raw = row?.DELIVERY_DATE ?? row?.delivery_date ?? row?.deliveryDate ?? null;
                const normalized = raw === null || raw === undefined ? '' : String(raw).trim();
                return normalized ? normalized : null;
            })(),
            isCurrent: row?.IS_CURRENT ?? row?.is_current ?? row?.isCurrent ?? null
        };
    }

    private normalizeListResponse(response: any, fallbackPage = 1, fallbackPerPage = 10): { items: any[]; meta: { total: number; page: number; perPage: number } } {
        const dataCandidates = [
            response?.data?.data,
            response?.data?.records,
            response?.data,
            response?.records,
            response?.meta?.data,
            response
        ];
        const items = dataCandidates.find(Array.isArray) || [];
        const metaSource = response?.meta || response?.data?.meta || response?.data?.metadata || {};
        const perPage = Number(metaSource.per_page ?? metaSource.perPage ?? fallbackPerPage);
        const page = Number(metaSource.page ?? metaSource.current_page ?? fallbackPage);
        const totalRaw = Number(metaSource.total ?? metaSource.total_records ?? metaSource.count ?? metaSource.Total ?? metaSource.totalCount);

        const safePerPage = Number.isFinite(perPage) && perPage > 0 ? perPage : fallbackPerPage;
        const safePage = Number.isFinite(page) && page > 0 ? page : fallbackPage;
        const safeTotal = Number.isFinite(totalRaw) && totalRaw >= 0
            ? totalRaw
            : (Array.isArray(items) ? items.length + (safePage - 1) * safePerPage : 0);

        return {
            items: Array.isArray(items) ? items : [],
            meta: {
                total: safeTotal,
                page: safePage,
                perPage: safePerPage
            }
        };
    }

    createProject(project: Record<string, unknown>): Observable<Project> {
        return this.api.post<any>(this.AUTH_PATH, project).pipe(
            map((response) => this.mapProject(response?.data || response))
        );
    }

    updateProject(id: string, project: Record<string, unknown>): Observable<Project> {
        return this.api.put<any>(`${this.AUTH_PATH}/${id}`, project).pipe(
            map((response) => this.mapProject(response?.data || response))
        );
    }

    // Per-project reservation hold settings (migration 163). Members come back
    // with the payload so the approver picker does not need a second call.
    getProjectHoldSettings(projectId: string): Observable<ProjectHoldSettings> {
        return this.api.get<any>(`${this.AUTH_PATH}/${projectId}/hold-settings`).pipe(
            map((response) => this.mapProjectHoldSettings(response?.data || response))
        );
    }

    updateProjectHoldSettings(projectId: string, settings: ProjectHoldSettingsPayload): Observable<ProjectHoldSettings> {
        return this.api.put<any>(`${this.AUTH_PATH}/${projectId}/hold-settings`, settings).pipe(
            map((response) => this.mapProjectHoldSettings(response?.data || response))
        );
    }

    private mapProjectHoldSettings(data: any): ProjectHoldSettings {
        const rawApprovers = data?.approver_user_ids ?? data?.approverUserIds;
        return {
            projectId: String(data?.project_id || data?.projectId || ''),
            orgId: data?.org_id ? String(data.org_id) : (data?.orgId ? String(data.orgId) : null),
            enabled: Boolean(data?.enabled),
            holdDays: Number(data?.hold_days ?? data?.holdDays ?? 2) || 2,
            extensionHours: Number(data?.extension_hours ?? data?.extensionHours ?? 24) || 24,
            approverUserIds: Array.isArray(rawApprovers) ? rawApprovers.map((id: any) => String(id)) : [],
            updatedAt: data?.updated_at ?? data?.updatedAt ?? null,
            members: Array.isArray(data?.members) ? data.members : []
        };
    }

    draftProjectProperties(projectId: string): Observable<void> {
        return this.api.post<any>(`${this.AUTH_PATH}/${projectId}/properties/draft`, {}).pipe(
            map(() => undefined)
        );
    }

    approveProjectProperties(projectId: string): Observable<void> {
        return this.api.post<any>(`${this.AUTH_PATH}/${projectId}/properties/approve`, {}).pipe(
            map(() => undefined)
        );
    }

    /**
     * Publish every eligible unit of a project to the public API. Separate from
     * approveProjectProperties (which only verifies) because Verified does not
     * imply Published. Super Admin only (`properties:publish`).
     */
    publishProjectProperties(projectId: string): Observable<{ ok: boolean; updated: number }> {
        return this.api.post<{ ok: boolean; updated: number }>(
            `${this.AUTH_PATH}/${projectId}/properties/publish`,
            {}
        );
    }

    deleteProject(id: string): Observable<void> {
        return this.api.delete<void>(`${this.AUTH_PATH}/${id}`);
    }

    uploadProjectImage(projectId: string, file: File): Observable<Project> {
        const body = new FormData();
        body.append('image', file, file.name);
        // Let the browser supply Content-Type and its multipart boundary.
        return this.api.post<any>(`${this.AUTH_PATH}/${projectId}/image`, body).pipe(
            map((response) => this.mapProject(response?.data || response))
        );
    }

    // ---------------------------------------------
    // Offer slides (API2)
    // ---------------------------------------------

    listOfferSlides(
        projectId: string,
        kind?: OfferSlideKind,
        options?: { scope?: 'project' | 'unit'; propertyId?: string | null }
    ): Observable<ProjectOfferSlide[]> {
        const id = encodeURIComponent(String(projectId || '').trim());
        if (!id) throw new Error('Missing project id');
        let params = new HttpParams();
        if (kind) params = params.set('kind', String(kind));
        // Scope separation (#3): project-level (property_id NULL) vs unit-level.
        if (options?.scope === 'project') {
            params = params.set('scope', 'project');
        } else if (options?.scope === 'unit' || options?.propertyId) {
            params = params.set('scope', 'unit');
            const pid = String(options?.propertyId || '').trim();
            if (pid) params = params.set('property_id', pid);
        }
        return this.api.get<any>(`${this.AUTH_PATH}/${id}/offer-slides`, params).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.DATA) ? resp.DATA : [];
                return rows.map((r: any) => ({
                    id: String(r?.id ?? r?.ROWID ?? ''),
                    project_id: String(r?.project_id ?? projectId),
                    property_id: r?.property_id ? String(r.property_id) : null,
                    kind: String(r?.kind ?? ''),
                    file_id: String(r?.file_id ?? ''),
                    sort_order: Number(r?.sort_order ?? 0),
                    meta_json: r?.meta_json ?? null,
                    width_px: r?.width_px !== null && r?.width_px !== undefined ? Number(r.width_px) : null,
                    height_px: r?.height_px !== null && r?.height_px !== undefined ? Number(r.height_px) : null,
                    aspect_label: r?.aspect_label ? String(r.aspect_label) : null,
                    url: r?.url !== null && r?.url !== undefined ? String(r.url) : null,
                    created_at: r?.created_at ?? null
                }));
            })
        );
    }

    createOfferSlide(
        projectId: string,
        payload: {
            kind: OfferSlideKind;
            file_id: string;
            sort_order?: number | null;
            meta_json?: any | null;
            property_id?: string | null;
        }
    ): Observable<ProjectOfferSlide> {
        const id = encodeURIComponent(String(projectId || '').trim());
        if (!id) throw new Error('Missing project id');
        const body: any = {
            kind: payload.kind,
            file_id: payload.file_id
        };
        if (payload.sort_order !== undefined) body.sort_order = payload.sort_order;
        if (payload.meta_json !== undefined) body.meta_json = payload.meta_json;
        // Unit-level slides carry property_id; project-level slides omit it (NULL).
        if (payload.property_id) body.property_id = String(payload.property_id);
        return this.api.post<any>(`${this.AUTH_PATH}/${id}/offer-slides`, body).pipe(
            map((resp) => {
                const row = resp?.data ?? resp?.DATA ?? resp ?? {};
                return {
                    id: String(row?.id ?? ''),
                    project_id: String(row?.project_id ?? projectId),
                    property_id: row?.property_id ? String(row.property_id) : null,
                    kind: String(row?.kind ?? payload.kind),
                    file_id: String(row?.file_id ?? payload.file_id),
                    sort_order: Number(row?.sort_order ?? 0),
                    meta_json: row?.meta_json ?? null,
                    width_px: row?.width_px !== null && row?.width_px !== undefined ? Number(row.width_px) : null,
                    height_px: row?.height_px !== null && row?.height_px !== undefined ? Number(row.height_px) : null,
                    aspect_label: row?.aspect_label ? String(row.aspect_label) : null,
                    url: row?.url !== null && row?.url !== undefined ? String(row.url) : null,
                    created_at: row?.created_at ?? null
                };
            })
        );
    }

    reorderOfferSlides(projectId: string, kind: OfferSlideKind, ids: string[]): Observable<void> {
        const id = encodeURIComponent(String(projectId || '').trim());
        if (!id) throw new Error('Missing project id');
        return this.api.put<any>(`${this.AUTH_PATH}/${id}/offer-slides/reorder`, { kind, ids }).pipe(map(() => undefined));
    }

    deleteOfferSlide(projectId: string, slideId: string): Observable<void> {
        const pid = encodeURIComponent(String(projectId || '').trim());
        const sid = encodeURIComponent(String(slideId || '').trim());
        if (!pid) throw new Error('Missing project id');
        if (!sid) throw new Error('Missing slide id');
        return this.api.delete<any>(`${this.AUTH_PATH}/${pid}/offer-slides/${sid}`).pipe(map(() => undefined));
    }
}
